import React from 'react';
import CaretIcon from '@hero/ui-kit/icons/utility/CaretIcon';
import { formatDayLabel, formatSlotTime, isUsableZone, zoneLabel } from './timeFormat';
import { TimeSlot, AppointmentHours } from '../../../api/useAvailableSlots';
import * as Style from './style.module.scss';

// HWM-22633: port of hero-storefront-app's SlotPicker. Date arrows +
// horizontal pill grid. Used by ScheduleMedicalCheckModal for the
// DrChrono / Polycare booking flow.
//
// HWM-23070: opens on the first bookable day (it used to open on the first
// key in `slots`, which can be fully booked) and renders slots in the
// clinic's timezone from their UTC instants.

interface SlotPickerProps {
    slots: Record<string, TimeSlot[]>;
    appointmentHours: Record<string, AppointmentHours | null>;
    clinicTimezone: string;
    firstAvailableDate?: string | null;
    onSlotSelect: (date: string, slot: TimeSlot) => void;
    selectedSlot?: { date: string; time: string } | null;
}

const SlotPicker: React.FC<SlotPickerProps> = ({
    slots,
    appointmentHours,
    clinicTimezone,
    firstAvailableDate,
    onSlotSelect,
    selectedSlot
}) => {
    const dates = React.useMemo(() => Object.keys(slots).sort(), [slots]);

    // Null until the care manager arrows to a day: the shown day then tracks
    // `first_available_date` across a refetch (a cached-then-revalidated open
    // would otherwise freeze the picker on a day that has since filled up),
    // and sticks once they have chosen one.
    const [chosenIndex, setChosenIndex] = React.useState<number | null>(null);
    const firstAvailableIndex = React.useMemo(() => {
        const i = firstAvailableDate ? dates.indexOf(firstAvailableDate) : -1;
        return i >= 0 ? i : 0;
    }, [dates, firstAvailableDate]);

    // Guards a refetch returning fewer days than the current index.
    const safeIndex = Math.min(chosenIndex ?? firstAvailableIndex, dates.length - 1);
    const activeDate = dates[safeIndex];
    const activeSlots = activeDate ? slots[activeDate] || [] : [];
    const activeHours = activeDate ? appointmentHours[activeDate] : null;

    const canGoPrev = safeIndex > 0;
    const canGoNext = safeIndex < dates.length - 1;

    // Abbreviation is DST-dependent, so it is read off an actual slot instant —
    // any day's, so the heading holds steady while arrowing past closed days.
    const anchorIso = React.useMemo(
        () =>
            Object.values(slots)
                .flat()
                .find((slot) => slot.start_utc)?.start_utc,
        [slots]
    );
    const clinicZone =
        anchorIso && isUsableZone(clinicTimezone)
            ? zoneLabel(anchorIso, clinicTimezone)
            : clinicTimezone;

    if (dates.length === 0) {
        return <p className={Style.subtitle}>No available times found. Please try again later.</p>;
    }

    return (
        <div>
            {clinicZone ? (
                <p className={Style.zoneNote}>Times in clinic time — {clinicZone}</p>
            ) : null}

            {firstAvailableDate === null ? (
                <p className={Style.subtitle}>
                    No openings in the clinic&apos;s search window — every slot below is booked.
                </p>
            ) : null}

            <div className={Style.dayNav}>
                <button
                    type="button"
                    className={Style.dayNavArrow}
                    onClick={() => setChosenIndex(safeIndex - 1)}
                    disabled={!canGoPrev}
                    aria-label="Previous day"
                >
                    <CaretIcon direction="backward" size="utility" />
                </button>
                <span className={Style.dayNavLabel}>{formatDayLabel(activeDate)}</span>
                <button
                    type="button"
                    className={Style.dayNavArrow}
                    onClick={() => setChosenIndex(safeIndex + 1)}
                    disabled={!canGoNext}
                    aria-label="Next day"
                >
                    <CaretIcon direction="forward" size="utility" />
                </button>
            </div>

            {activeSlots.length > 0 ? (
                <div className={Style.slotGrid}>
                    {activeSlots.map((slot) => {
                        const isSelected =
                            selectedSlot?.date === activeDate && selectedSlot?.time === slot.time;
                        return (
                            <button
                                key={slot.time}
                                type="button"
                                className={isSelected ? Style.slotButtonSelected : Style.slotButton}
                                disabled={!slot.available}
                                onClick={() => onSlotSelect(activeDate, slot)}
                            >
                                {formatSlotTime(slot, clinicTimezone)}
                            </button>
                        );
                    })}
                </div>
            ) : (
                <p className={Style.subtitle}>
                    {activeHours
                        ? 'All time slots are booked for this day.'
                        : 'Clinic is closed on this day.'}
                </p>
            )}
        </div>
    );
};

export default SlotPicker;
